import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import liveEyeIcon from "../../assets/images/liveEyeIcon.png";
import { toast } from "../../components/Toast/toast.ts";
import { ProgressSpinner } from "primereact/progressspinner";
import { Camera, Store, ChevronRight, User, Phone, Lock } from "lucide-react";
import { useUserContext } from "../../context/UserContext.tsx";
import { useNavigate } from "react-router-dom";
import { StatusChip } from "../../components/StatusChip/index.ts";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import AppButton from "../../components/ui/AppButton.tsx";

const ProfilePage = () => {
  const { setReload, reload, user, setUserDetails } = useUserContext();
  const firstNameInput = useRef<HTMLInputElement | null>(null);
  const lastNameInput = useRef<HTMLInputElement | null>(null);
  const phoneNumberInput = useRef<HTMLInputElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || "");
  const navigate = useNavigate();

  // Sync local state when user context updates
  useEffect(() => {
    if (user) {
      setFirstName(user?.firstName || "");
      setLastName(user?.lastName || "");
      setPhoneNumber(user?.phoneNumber || "");
    }
  }, [user]);

  function handleClick() {
    photoInputRef?.current?.click();
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  async function updateUserData(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const storedToken = localStorage.getItem("token");

    const firstName = firstNameInput.current?.value;
    const lastName = lastNameInput.current?.value;

    // Check if firstName or lastName is empty
    if (!firstName || !lastName) {
      toast.error("Both First Name and Last Name are required!");
      return;
    }

    if (storedToken) {
      setLoading(true);
      try {
        let payload: any;
        if (selectedImage) {
          payload = {
            firstName,
            lastName,
            phoneNumber,
            mimetype: selectedImage.type,
            originalName: selectedImage.name,
          };
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/update-user`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${storedToken}`,
                "Content-Type": "application/json",
              },
            },
          );
          await axios.put(response?.data?.uploadUrl, selectedImage, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
              "Content-Type": selectedImage.type,
            },
          });
        } else {
          payload = { firstName, lastName, phoneNumber, profilePic: "" };
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/update-user`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${storedToken}`,
                "Content-Type": "application/json",
              },
            },
          );
        }
        // Update context immediately so UI reflects changes without refresh
        setUserDetails({ ...user, firstName, lastName, phoneNumber });
        toast.success("Profile updated successfully!");
        setReload(!reload);
      } catch (error) {
        toast.error("Failed to update profile.");
      } finally {
        setLoading(false);
      }
    }
  }

  const inputClass =
    "w-full h-[42px] px-3 text-[13px] rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-1))] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))] outline-none focus:border-gold-2 focus:ring-1 focus:ring-gold-2/20 transition-all";
  const labelClass =
    "text-[11px] font-semibold uppercase tracking-[0.06em] text-[rgb(var(--text-muted))] mb-1.5 block";

  return (
    <div className="flex flex-col gap-6">

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── Left: Profile Card ─── */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          {/* Avatar */}
          <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-6 flex flex-col items-center gap-4">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[rgb(var(--border-secondary))] ring-4 ring-[rgb(var(--surface-2))]">
                <img
                  src={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : user?.profileImage
                  }
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={handleClick}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gold-2 hover:bg-gold-3 flex items-center justify-center shadow-md transition-colors"
              >
                <Camera size={14} className="text-white" />
              </button>
              <input
                type="file"
                ref={photoInputRef}
                className="hidden"
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>
            <div className="text-center">
              <p className="font-semibold text-[rgb(var(--text-primary))] text-[15px]">
                {user ? `${user?.firstName} ${user?.lastName}` : "—"}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] p-6 flex flex-col gap-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <ProgressSpinner
                  style={{ width: "50px", height: "50px" }}
                  strokeWidth="3"
                  animationDuration=".5s"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <User size={10} />
                      First Name
                    </span>
                  </label>
                  <input
                    ref={firstNameInput}
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <User size={10} />
                      Last Name
                    </span>
                  </label>
                  <input
                    ref={lastNameInput}
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <Phone size={10} />
                      Phone Number
                    </span>
                  </label>
                  <input
                    ref={phoneNumberInput}
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className={inputClass}
                    placeholder="Enter phone number"
                  />
                </div>

                <AppButton
                  onClick={updateUserData}
                  appVariant="primary"
                  loading={loading}
                  sx={{
                    width: "100%",
                    height: "42px",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  Save Changes
                </AppButton>

                <div className="border-t border-[rgb(var(--border-primary))] pt-4">
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <Lock size={10} />
                      Password
                    </span>
                  </label>
                  <AppButton
                    onClick={() => navigate("/change-pwd")}
                    appVariant="secondary"
                    sx={{ width: "100%", height: "42px", fontSize: "13px" }}
                  >
                    Change Password
                  </AppButton>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ─── Right: Stores ─── */}
        <div className="lg:col-span-2 border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-5 py-4 border-b border-[rgb(var(--border-primary))]">
            <Store size={16} className="text-gold-2" />
            <span className="font-semibold text-[rgb(var(--text-primary))] text-[15px]">
              Stores
            </span>
            <span className="ml-auto text-[11px] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-2))] px-2 py-0.5 rounded-full">
              {user?.stores?.length || 0}
            </span>
          </div>

          {/* Store list */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 350px)" }}
          >
            {user?.stores?.map((store: any, index: number) => (
              <div
                key={store?._id}
                className="flex items-center gap-3 px-5 py-3.5 border-b border-[rgb(var(--border-primary))] hover:bg-[rgb(var(--surface-2))] transition-colors last:border-b-0 group"
              >
                {/* Icon */}
                <div className="w-9 h-9 rounded-full bg-gold-2/10 flex items-center justify-center shrink-0">
                  <img
                    src={liveEyeIcon}
                    alt=""
                    className="w-5 h-3.5 object-contain"
                  />
                </div>

                {/* Store info */}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[rgb(var(--text-primary))] truncate">
                    {store?.name}
                  </p>
                  <p className="text-[11px] text-[rgb(var(--text-muted))] truncate mt-0.5">
                    {[store?.address, store?.city, store?.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>

                {/* Payment status */}
                {store?.zoho_invoice?.unpaid_invoices_balance && (
                  <StatusChip
                    label={store.zoho_invoice.unpaid_invoices_balance}
                    variant={
                      store.zoho_invoice.unpaid_invoices_balance === "Paid"
                        ? "success"
                        : "danger"
                    }
                  />
                )}
              </div>
            ))}

            {(!(user as any)?.stores || (user as any).stores.length === 0) && (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Store
                  size={32}
                  className="text-[rgb(var(--text-muted))] opacity-30"
                />
                <p className="text-[13px] text-[rgb(var(--text-muted))]">
                  No stores assigned
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
